import { type ReactNode } from 'react';
import { Tooltip, TooltipContent } from '@customerio/pluma-components/react';

/**
 * Direct port of the Ember `<ViewerRestrictedTooltip>` (under
 * `~/code/ui/app/components/viewer-restricted-tooltip/index.gts`).
 *
 * Wraps a disabled-when-the-user-lacks-permission interactive element
 * (typically a Button or Link) and explains why it's disabled on hover.
 * Uses the `isCustom` Tooltip pattern under the hood — required so the
 * wrapper doesn't render its own interactive shell around a disabled
 * child (which would nest two buttons and suppress hover).
 *
 * Consumers pass a render function that receives the Floating UI
 * context PLUS the `readOnly` boolean, mirroring the Ember block
 * yield (`as |setReference readOnly|`). Spread
 * `context.reference.getReferenceProps()` onto the disabled element so
 * the hover/focus handlers connect through.
 *
 *   <ViewerRestrictedTooltip readOnly={!canCreate}>
 *     {(context) => (
 *       <Button
 *         isDisabled={!canCreate}
 *         href={canCreate ? href : undefined}
 *         ref={context.reference.setReference}
 *         {...context.reference.getReferenceProps()}
 *       >
 *         Create
 *       </Button>
 *     )}
 *   </ViewerRestrictedTooltip>
 *
 * **`readOnly` vs `restriction`** mirrors the Ember API: when
 * `restriction` is provided, the tooltip only fires if BOTH `readOnly`
 * and `restriction` are true. Useful when a control should appear
 * locked for two independent reasons but the "your role doesn't have
 * access" message only applies to one of them.
 */

// The Pluma `Tooltip` render-fn signature — re-derived from its docs
// rather than imported because Pluma doesn't export it directly. Keep
// in sync if the Pluma surface changes.
export interface ViewerRestrictedTooltipContext {
  reference: {
    setReference: (node: Element | null) => void;
    getReferenceProps: (userProps?: React.HTMLProps<Element>) => Record<string, unknown>;
  };
}

export interface ViewerRestrictedTooltipProps {
  /**
   * `true` when the user can't interact with the wrapped element
   * (typically because they lack a permission). Drives both the
   * `isDisabled` state on the trigger AND whether the tooltip
   * activates on hover.
   */
  readOnly: boolean;
  /**
   * Optional second predicate. When provided, the tooltip only shows
   * if `readOnly && restriction`. Lets callers gate the "your role
   * doesn't have access" message on a more specific check than just
   * "disabled".
   */
  restriction?: boolean;
  /**
   * Custom tooltip body. Defaults to the same copy the Ember component
   * renders. Override when the user lacks a more specific permission
   * and you want a more specific explanation.
   */
  message?: ReactNode;
  /**
   * Render the trigger element. Receives the Floating UI context plus
   * the resolved `readOnly` flag (so the consumer can wire
   * `isDisabled` etc without re-deriving it).
   */
  children: (context: ViewerRestrictedTooltipContext, readOnly: boolean) => ReactNode;
}

const DEFAULT_MESSAGE = 'Your user role does not have access to this feature.';

export function ViewerRestrictedTooltip({
  readOnly,
  restriction,
  message = DEFAULT_MESSAGE,
  children,
}: ViewerRestrictedTooltipProps) {
  // Match the Ember `showTooltip` getter: with no `restriction` arg,
  // fall through to `readOnly`; with one, require both.
  const showTooltip = restriction == null ? readOnly : readOnly && restriction;
  return (
    <Tooltip isCustom isDisabled={!showTooltip}>
      {(context: ViewerRestrictedTooltipContext) => (
        <>
          {children(context, readOnly)}
          <TooltipContent>{message}</TooltipContent>
        </>
      )}
    </Tooltip>
  );
}
